import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getLudopediaAuthUrl, clearUserToken, isTokenValid, registerManualAccessToken } from '../lib/services/ludopediaAuthService';
import { getLudopediaUserData, getLudopediaCollection, importLudopediaCollection } from '../lib/services/ludopediaService';
import { LudopediaUser, LudopediaCollection } from '../types';

/**
 * Hook para facilitar o uso da integração com a Ludopedia
 */
export function useLudopedia() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ludopediaUser, setLudopediaUser] = useState<LudopediaUser | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState<'oauth' | 'manual'>('oauth');
  
  // Verificar se o usuário está conectado à Ludopedia
  useEffect(() => {
    const checkConnection = async () => {
      if (!user) {
        setLoading(false);
        setIsConnected(false);
        setLudopediaUser(null);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Verificar se o token é válido
        const valid = await isTokenValid(user.id);
        
        if (!valid) {
          setIsConnected(false);
          setLudopediaUser(null);
          setLoading(false);
          return;
        }
        
        // Obter dados do usuário na Ludopedia
        const userData = await getLudopediaUserData(user.id);
        
        if (userData) {
          setLudopediaUser(userData);
          setIsConnected(true);
        } else {
          setLudopediaUser(null);
          setIsConnected(false);
        }
      } catch (err) {
        console.error('Erro ao verificar conexão com Ludopedia:', err);
        setError(err instanceof Error ? err.message : 'Erro ao verificar conexão com Ludopedia');
        setIsConnected(false);
        setLudopediaUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkConnection();
  }, [user]);
  
  /**
   * Inicia o fluxo de autenticação da Ludopedia via OAuth
   */
  const connect = () => {
    if (!user) {
      setError('Você precisa estar logado para conectar sua conta da Ludopedia');
      return;
    }
    
    try {
      // Definir método de autenticação como OAuth
      setAuthMethod('oauth');
      
      const authUrl = getLudopediaAuthUrl();
      window.location.href = authUrl;
    } catch (err) {
      console.error('Erro ao iniciar autenticação da Ludopedia:', err);
      setError(err instanceof Error ? err.message : 'Erro ao iniciar autenticação da Ludopedia');
    }
  };
  
  /**
   * Conecta à Ludopedia usando um token fornecido manualmente
   * Útil quando o fluxo OAuth está falhando
   * @param accessToken Token de acesso fornecido pelo usuário
   */
  const connectWithAccessToken = async (accessToken: string): Promise<boolean> => {
    if (!user) {
      setError('Você precisa estar logado para conectar sua conta da Ludopedia');
      return false;
    }
    
    if (!accessToken || accessToken.trim() === '') {
      setError('Token de acesso é obrigatório');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Definir método de autenticação como manual
      setAuthMethod('manual');
      
      const result = await registerManualAccessToken(user.id, accessToken);
      
      if (result) {
        // Recarregar as informações do usuário
        const userData = await getLudopediaUserData(user.id);
        
        if (userData) {
          setLudopediaUser(userData);
          setIsConnected(true);
          return true;
        }
      }
      
      setError('Não foi possível registrar o token de acesso. Verifique se o token é válido.');
      return false;
    } catch (err) {
      console.error('Erro ao conectar com token manual:', err);
      setError(err instanceof Error ? err.message : 'Erro ao conectar com token manual');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Desconecta a conta da Ludopedia
   */
  const disconnect = async () => {
    if (!user) {
      setError('Você precisa estar logado para desconectar sua conta da Ludopedia');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await clearUserToken(user.id);
      
      if (result) {
        setLudopediaUser(null);
        setIsConnected(false);
        return true;
      } else {
        setError('Erro ao desconectar conta da Ludopedia');
        return false;
      }
    } catch (err) {
      console.error('Erro ao desconectar conta da Ludopedia:', err);
      setError(err instanceof Error ? err.message : 'Erro ao desconectar conta da Ludopedia');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Busca a coleção de jogos do usuário na Ludopedia
   */
  const getCollection = async (page: number = 1, perPage: number = 50): Promise<LudopediaCollection | null> => {
    if (!user) {
      setError('Você precisa estar logado para buscar sua coleção na Ludopedia');
      return null;
    }
    
    if (!isConnected) {
      setError('Você precisa conectar sua conta da Ludopedia para buscar sua coleção');
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const collection = await getLudopediaCollection(user.id, page, perPage);
      return collection;
    } catch (err) {
      console.error('Erro ao buscar coleção na Ludopedia:', err);
      setError(err instanceof Error ? err.message : 'Erro ao buscar coleção na Ludopedia');
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Importa a coleção de jogos do usuário na Ludopedia
   */
  const importCollection = async () => {
    if (!user) {
      setError('Você precisa estar logado para importar sua coleção da Ludopedia');
      return { success: false, error: 'Usuário não logado' };
    }
    
    if (!isConnected) {
      setError('Você precisa conectar sua conta da Ludopedia para importar sua coleção');
      return { success: false, error: 'Conta da Ludopedia não conectada' };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await importLudopediaCollection(user.id);
      return result;
    } catch (err) {
      console.error('Erro ao importar coleção da Ludopedia:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao importar coleção da Ludopedia';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
  
  return {
    loading,
    error,
    isConnected,
    ludopediaUser,
    authMethod,
    connect,
    connectWithAccessToken,
    disconnect,
    getCollection,
    importCollection
  };
} 